import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BookOpen, Mic, Sparkles, Lock, ChevronRight, Globe2, GraduationCap } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const features = [
  { icon: BookOpen, title: "landing.f1_title", text: "landing.f1_text" },
  { icon: Mic, title: "landing.f2_title", text: "landing.f2_text" },
  { icon: Sparkles, title: "landing.f3_title", text: "landing.f3_text" },
];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-background">
      {/* HERO BAND — navy */}
      <div className="bg-primary text-primary-foreground relative overflow-hidden">
        <div className="h-1.5 w-full bg-secondary" />
        <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-white/5 blur-3xl" />

        {/* nav */}
        <header className="relative max-w-7xl mx-auto px-4 sm:px-8 h-24 flex items-center justify-between">
          <Logo size="md" chip />
          <div className="flex items-center gap-3">
            <LanguageToggle onDark />
            <Button data-testid="landing-login-button" size="lg" onClick={go}
              className="rounded-full font-bold h-12 px-6 bg-secondary text-secondary-foreground hover:bg-secondary/90">
              {t("landing.cta_login")}
            </Button>
          </div>
        </header>

        {/* hero content */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-10 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-secondary mb-6">
              <Globe2 className="h-4 w-4" /> {t("landing.bilingual")}
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.03]">
              {t("landing.hero_title")}
            </h1>
            <p className="text-lg sm:text-xl text-white/75 mt-7 max-w-xl leading-relaxed">
              {t("landing.hero_sub")}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Button size="lg" data-testid="landing-hero-login" onClick={go}
                className="rounded-full font-bold text-lg h-16 px-10 bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-transform hover:-translate-y-0.5 shadow-xl shadow-black/20">
                {t("landing.cta_login")} <ChevronRight className="h-5 w-5 ml-1" />
              </Button>
            </div>
            <div className="flex items-start gap-2 mt-7 text-sm text-white/60 max-w-md">
              <Lock className="h-4 w-4 mt-0.5 shrink-0 text-secondary" />
              <span>{t("landing.private_note")}</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
            <div className="absolute -inset-4 bg-secondary/30 blur-2xl rounded-[2.5rem]" />
            <img
              src={HERO_IMG}
              alt={t("landing.hero_title")}
              className="relative rounded-[2rem] border-4 border-secondary shadow-2xl w-full object-cover aspect-[5/4]"
            />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center"><GraduationCap className="h-6 w-6 text-secondary" /></div>
              <div className="leading-tight">
                <p className="font-heading font-extrabold text-primary text-lg">ES · EN</p>
                <p className="text-xs text-muted-foreground">A1 — C2</p>
              </div>
            </div>
          </motion.div>
        </section>
      </div>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="grid sm:grid-cols-3 gap-7">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-card rounded-[1.75rem] border border-border p-9 shadow-[0_18px_50px_rgb(20,38,77,0.08)] hover:-translate-y-2 transition-transform">
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-6 bg-primary ring-4 ring-secondary/20">
                <f.icon className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-primary">{t(f.title)}</h3>
              <p className="text-base text-muted-foreground mt-3 leading-relaxed">{t(f.text)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-primary text-primary-foreground py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <Logo size="sm" chip />
          <p className="text-sm text-white/60 text-center">{t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}
